import React from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { WorkflowPreview, WorkflowPreviewSection } from '../../types';

interface WorkflowPreviewViewProps {
  preview: WorkflowPreview;
  /** The AI-written sample for the `post` section, once generated. */
  sample?: string;
  sampleLoading: boolean;
  sampleError: string;
  onGenerateSample: () => void;
}

const CARD = {
  border: '1px solid #e5e7eb',
  borderRadius: 2,
  bgcolor: '#fff',
  p: 2,
  mb: 2,
} as const;

const Title: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontWeight: 700, fontSize: 13, letterSpacing: 0.3, color: '#374151', mb: 1 }}>
    {children}
  </Typography>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>{children}</Typography>
);

/** Times arrive as UTC instants; show them in the visitor's own time zone. */
const formatWhen = (iso: string) =>
  // Explicit fields: `dateStyle`/`timeStyle` cannot be combined with `timeZoneName`.
  new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(iso));

const PostCard: React.FC<{
  section: Extract<WorkflowPreviewSection, { kind: 'post' }>;
  sample?: string;
  sampleLoading: boolean;
  sampleError: string;
  onGenerateSample: () => void;
}> = ({ section, sample, sampleLoading, sampleError, onGenerateSample }) => (
  <Box sx={CARD}>
    <Title>{section.title}</Title>

    {/* A LinkedIn-style frame: fixed, so only the text inside it changes. */}
    <Box sx={{ border: '1px solid #d1d5db', borderRadius: 2, p: 1.5, bgcolor: '#f9fafb' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#c7d2fe' }} />
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>You</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Just now · LinkedIn</Typography>
        </Box>
      </Box>

      {sample ? (
        <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55 }}>{sample}</Typography>
      ) : (
        <Box sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: 14, lineHeight: 1.6 }}>
          {section.layout.map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, pt: 1, borderTop: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Like · Comment · Repost · Send</Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: sample && sample.length > section.charLimit ? '#b42318' : 'text.secondary',
          }}
        >
          {sample ? `${sample.length} / ${section.charLimit}` : `up to ${section.charLimit} characters`}
        </Typography>
      </Box>
    </Box>

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
      <Button
        size="small"
        variant="outlined"
        onClick={onGenerateSample}
        disabled={sampleLoading}
        sx={{ textTransform: 'none' }}
      >
        {sampleLoading ? (
          <>
            <CircularProgress size={14} sx={{ mr: 1 }} /> Writing a sample…
          </>
        ) : sample ? (
          'Write another sample'
        ) : (
          'Write a sample post'
        )}
      </Button>
      {sampleError && <Typography sx={{ fontSize: 12.5, color: '#b42318' }}>{sampleError}</Typography>}
    </Box>
    {section.note && <Note>{section.note}</Note>}
  </Box>
);

/** Renders a workflow preview. Every section arrives as data, so nothing here is HTML from the server. */
export const WorkflowPreviewView: React.FC<WorkflowPreviewViewProps> = ({
  preview,
  sample,
  sampleLoading,
  sampleError,
  onGenerateSample,
}) => (
  <Box>
    {preview.problems.length > 0 && (
      <Alert severity="info" sx={{ mb: 2 }}>
        Still needed to create the workflow: {preview.problems.map((p) => p.message).join('; ')}.
      </Alert>
    )}
    {preview.warnings.map((warning) => (
      <Alert key={warning} severity="warning" sx={{ mb: 1.5 }}>
        {warning}
      </Alert>
    ))}

    {preview.sections.map((section, index) => {
      switch (section.kind) {
        case 'rows':
          return (
            <Box key={index} sx={CARD}>
              <Title>{section.title}</Title>
              {section.rows.map((row) => (
                <Box key={row.label} sx={{ display: 'flex', gap: 1.5, py: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', minWidth: 110 }}>{row.label}</Typography>
                  <Typography sx={{ fontSize: 13.5, wordBreak: 'break-word' }}>{row.value}</Typography>
                </Box>
              ))}
            </Box>
          );
        case 'text':
          return (
            <Box key={index} sx={CARD}>
              <Title>{section.title}</Title>
              <Typography sx={{ fontSize: 13.5 }}>{section.body}</Typography>
              {section.note && <Note>{section.note}</Note>}
            </Box>
          );
        case 'email':
          return (
            <Box key={index} sx={CARD}>
              <Title>{section.title}</Title>
              <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1.5, p: 1.5, bgcolor: '#f9fafb' }}>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>To: {section.to}</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 14, my: 0.5 }}>{section.subject}</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.55 }}>{section.body}</Typography>
              </Box>
              {section.note && <Note>{section.note}</Note>}
            </Box>
          );
        case 'schedule':
          return (
            <Box key={index} sx={CARD}>
              <Title>{section.title}</Title>
              {section.items.map((item) => (
                <Box key={item.at} sx={{ display: 'flex', gap: 1.5, py: 0.5 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, minWidth: 110 }}>{item.label}</Typography>
                  <Typography sx={{ fontSize: 13.5 }}>{formatWhen(item.at)}</Typography>
                </Box>
              ))}
              {section.note && <Note>{section.note}</Note>}
            </Box>
          );
        case 'post':
          return (
            <PostCard
              key={index}
              section={section}
              sample={sample}
              sampleLoading={sampleLoading}
              sampleError={sampleError}
              onGenerateSample={onGenerateSample}
            />
          );
        default:
          return null;
      }
    })}
  </Box>
);

export default WorkflowPreviewView;
